import { useCallback } from 'react';
import type { DashboardFase } from '@/api/projectFases';
import type { ProjectTask } from '@/types/odoo';
import { InterventieBadge } from '@/components/interventie/InterventieBadge';
import { interventieFaseIds } from '@/utils/interventiePhotos';

interface FaseTabBarProps {
  fases: DashboardFase[];
  activeFaseId: number | 'alles' | null;
  onSelect: (faseId: number | 'alles') => void;
  /** Open the "new fase" dialog. When provided, a trailing + button is rendered. */
  onAddFase?: () => void;
  /**
   * Open the rename/delete dialog for a fase. When provided, the ACTIVE fase tab
   * grows a pencil button beside it. Only the active tab carries it: the strip
   * stays readable with many fases, and it works on a tablet, where there is no
   * hover to reveal a per-tab affordance. Omit for read-only roles.
   */
  onEditFase?: (fase: DashboardFase) => void;
  /** The project's tasks: a fase holding an Odoo `Interventie` tag gets a small badge. */
  projectTasks?: ProjectTask[];
}

/**
 * Horizontal, scrollable fase tab bar for the dashboard (Fase 2 + 6c). With ≥2
 * fases an "Alles" tab is rendered FIRST — it selects the all-fases project
 * roll-up (Fase 6c); single-fase projects never get an Alles tab. When
 * `onAddFase` is set, a trailing "+ Fase toevoegen" button is always rendered,
 * so the bar is shown even for 0–1-fase projects (DashboardTab no longer gates
 * the bar on ≥2 fases).
 */
export function FaseTabBar({ fases, activeFaseId, onSelect, onAddFase, onEditFase, projectTasks = [] }: FaseTabBarProps) {
  const interventie = interventieFaseIds(projectTasks);
  // Scroll the active tab into view when it mounts/changes so a late-ordered fase
  // is never left off-screen. Only the bar scrolls, sideways: scrollIntoView would
  // also scroll the page down to a bar below the fold when a project opens.
  const scrollActiveIntoView = useCallback((node: HTMLButtonElement | null) => {
    const bar = node?.closest<HTMLElement>('[data-fase-scroller]');
    if (!node || !bar) return;
    const tab = node.getBoundingClientRect();
    const view = bar.getBoundingClientRect();
    if (tab.left < view.left) bar.scrollLeft -= view.left - tab.left;
    else if (tab.right > view.right) bar.scrollLeft += tab.right - view.right;
  }, []);

  // Only projects with ≥2 fases get the "Alles" roll-up tab.
  const showAlles = fases.length >= 2;

  const tabClass = (isActive: boolean) =>
    `whitespace-nowrap rounded-lg px-3 py-2 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-td-blue focus-visible:ring-offset-1 ${
      isActive ? 'bg-td-blue text-white' : 'bg-gray-100 text-td-navy hover:bg-gray-200'
    }`;

  return (
    <div data-fase-scroller className="-mx-1 overflow-x-auto">
      <nav aria-label="Fases" className="flex min-w-max gap-1 px-1">
        {showAlles && (
          <button
            type="button"
            ref={activeFaseId === 'alles' ? scrollActiveIntoView : undefined}
            aria-current={activeFaseId === 'alles' ? 'page' : undefined}
            onClick={() => onSelect('alles')}
            className={tabClass(activeFaseId === 'alles')}
          >
            Alles
          </button>
        )}
        {fases.map((fase) => {
          const isActive = fase.id === activeFaseId;
          const editable = Boolean(onEditFase) && isActive;
          return (
            // A tab and its pencil are two separate buttons (a button may not be
            // nested in a button), grouped so they read as one pill.
            <span key={fase.id} className="flex items-stretch">
              <button
                type="button"
                ref={isActive ? scrollActiveIntoView : undefined}
                aria-current={isActive ? 'page' : undefined}
                onClick={() => onSelect(fase.id)}
                className={editable ? `${tabClass(isActive)} rounded-r-none` : tabClass(isActive)}
              >
                {fase.name}
                {interventie.has(fase.id) && <InterventieBadge className="ml-1.5 align-middle" />}
              </button>
              {editable && (
                <button
                  type="button"
                  onClick={() => onEditFase?.(fase)}
                  aria-label={`Fase ${fase.name} bewerken of verwijderen`}
                  title="Fase bewerken of verwijderen"
                  className="rounded-lg rounded-l-none border-l border-white/25 bg-td-blue px-2 text-white transition-colors hover:bg-td-blue/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-td-blue focus-visible:ring-offset-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                </button>
              )}
            </span>
          );
        })}
        {onAddFase && (
          <button
            type="button"
            onClick={onAddFase}
            className="whitespace-nowrap rounded-lg border border-dashed border-td-blue/50 px-3 py-2 text-sm font-semibold text-td-blue transition-colors hover:bg-td-blue/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-td-blue focus-visible:ring-offset-1"
          >
            + Fase toevoegen
          </button>
        )}
      </nav>
    </div>
  );
}
